/*
 * Adds Business Credit Application fields to the WPD wholesale signup form.
 *
 * WHY: The WPD app (Wholesale Helper) caps its own custom fields at 5 and only
 * offers text/file types, so it cannot reproduce Atelier's full credit
 * application. WPD's signup script reads a hidden #ExtraFields input and appends
 * its value to the new customer's note on submit — a deliberate extension hook.
 * We inject the remaining application fields and mirror their values into
 * #ExtraFields, so WPD still creates + approves + tags the account while the
 * extra details land on the customer record. No data is posted to WPD's private
 * endpoint directly; we reuse its own submit handler.
 *
 * The injected fields are built as native WPD sections (wpd-secondary-header +
 * a two-column grid matching wpd's own layout) so they read as part of the form.
 */
(function () {
  'use strict';

  // full: field spans both grid columns (radio row, shipping textarea).
  var SECTIONS = [
    { title: 'Additional Business Details', fields: [
      { id: 'aeg_dba', label: 'DBA (Doing Business As)', type: 'text', placeholder: 'DBA' },
      { id: 'aeg_fax', label: 'Fax', type: 'tel', placeholder: 'Fax' },
      { id: 'aeg_mobile', label: 'Mobile', type: 'tel', placeholder: 'Mobile' },
      { id: 'aeg_business_type', label: 'Type of Business', type: 'radio', options: ['Corporation', 'Sole Proprietorship'], full: true }
    ] },
    { title: 'Accounts Payable Contact', fields: [
      { id: 'aeg_ap_name', label: 'A/P Contact Name', type: 'text', placeholder: 'Accounts payable contact' },
      { id: 'aeg_ap_email', label: 'A/P Contact Email', type: 'email', placeholder: 'ap@companyname.com' }
    ] },
    { title: 'Shipping Address (if different than billing)', fields: [
      { id: 'aeg_shipping', label: 'Shipping Address', type: 'textarea', placeholder: 'Shipping address if different from billing', full: true }
    ] }
  ];

  // Secondary contact / reference fields must not trigger browser autofill of the
  // applicant's own details, so autocomplete stays off; inputmode aids mobile keyboards.
  var INPUTMODE = { tel: 'tel', email: 'email' };

  function el(tag, attrs, children) {
    var e = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'class') e.className = attrs[k];
      else e.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) {
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  function buildField(f) {
    var wrap = el('div', { 'class': 'wpd-signup-field aeg-extra-field' + (f.full ? ' aeg-full' : ''), id: f.id + 'Wrapper' });

    if (f.type === 'radio') {
      wrap.appendChild(el('label', {}, [f.label]));
      var group = el('div', { 'class': 'aeg-radio-group' });
      f.options.forEach(function (opt, i) {
        var rid = f.id + '_' + i;
        var opLabel = el('label', { 'class': 'aeg-radio-option', 'for': rid }, [opt]);
        opLabel.insertBefore(el('input', { type: 'radio', name: f.id, id: rid, value: opt }), opLabel.firstChild);
        group.appendChild(opLabel);
      });
      wrap.appendChild(group);
      return wrap;
    }

    wrap.appendChild(el('label', { 'for': f.id }, [f.label]));
    var attrs = { id: f.id, placeholder: f.placeholder || '', autocomplete: 'off' };
    if (f.type === 'textarea') {
      attrs.rows = '3';
      wrap.appendChild(el('textarea', attrs));
    } else {
      attrs.type = f.type || 'text';
      if (INPUTMODE[f.type]) attrs.inputmode = INPUTMODE[f.type];
      wrap.appendChild(el('input', attrs));
    }
    return wrap;
  }

  function buildSection(s) {
    var section = el('div', { 'class': 'aeg-extra-section' });
    section.appendChild(el('span', { 'class': 'wpd-secondary-header' }, [s.title]));
    var grid = el('div', { 'class': 'aeg-fields-grid' });
    s.fields.forEach(function (f) { grid.appendChild(buildField(f)); });
    section.appendChild(grid);
    return section;
  }

  function collectValue(f) {
    if (f.type === 'radio') {
      var checked = document.querySelector('input[name="' + f.id + '"]:checked');
      return checked ? checked.value : '';
    }
    var e = document.getElementById(f.id);
    return e ? (e.value || '').trim() : '';
  }

  // Mirror the injected fields into #ExtraFields so WPD appends them to the
  // customer note when its own submit handler runs.
  function syncExtraFields() {
    var extra = document.getElementById('ExtraFields');
    if (!extra) return;
    var lines = [];
    SECTIONS.forEach(function (s) {
      var sectionLines = [];
      s.fields.forEach(function (f) {
        var v = collectValue(f);
        if (v) sectionLines.push(' ' + f.label + ': ' + v);
      });
      if (sectionLines.length) {
        lines.push('');
        lines.push('--- ' + s.title + ' ---');
        lines = lines.concat(sectionLines);
      }
    });
    extra.value = lines.length
      ? '\r\n\r\n===== ADDITIONAL APPLICATION DETAILS =====' + lines.map(function (l) { return '\r\n' + l; }).join('')
      : '';
  }

  function inject() {
    var extra = document.getElementById('ExtraFields');
    var submitWrap = document.getElementById('wpdSubmitButtonWrapper');
    if (!extra || !submitWrap) return false;
    if (document.getElementById('aegExtraInjected')) return true;

    var container = el('div', { id: 'aegExtraInjected' });
    SECTIONS.forEach(function (s) { container.appendChild(buildSection(s)); });

    // Sit the new sections after WPD's own fields, before the captcha + submit.
    var anchor = document.getElementById('wpdCaptchaWrapper') || submitWrap;
    anchor.parentNode.insertBefore(container, anchor);

    container.addEventListener('input', syncExtraFields);
    container.addEventListener('change', syncExtraFields);
    // Belt-and-suspenders: WPD reads #ExtraFields inside a deferred timeout after
    // this click, so a synchronous capture-phase listener is guaranteed to run first.
    var btn = document.getElementById('wpdSubmitButton');
    if (btn) btn.addEventListener('click', syncExtraFields, true);
    syncExtraFields();
    return true;
  }

  function start() {
    if (inject()) return;
    // WPD renders the form asynchronously; poll until it exists.
    var tries = 0;
    var iv = setInterval(function () {
      if (inject() || ++tries > 60) clearInterval(iv);
    }, 500);
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
